import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { useAppConfiguration } from '@mews-commander/data-access';
import { PlatformEnvironment } from '@mews/enums';
import { ErrorHandler } from './ErrorHandler';
import { SuspenseBoundary } from './SuspenseBoundary';

interface QueryBoundaryProps extends React.ComponentProps<typeof SuspenseBoundary> {
    showError?: boolean;
    renderErrorMessage?: (error: Error) => React.ReactNode;
    renderErrorTitle?: (error: Error) => React.ReactNode;
}

export const QueryBoundary = ({
    children,
    loader,
    showError = true,
    renderErrorMessage,
    renderErrorTitle,
}: React.PropsWithChildren<QueryBoundaryProps>) => {
    const { environment } = useAppConfiguration();
    const showStackTrace = environment !== PlatformEnvironment.Production && environment !== PlatformEnvironment.Demo;

    const errorBoundary = (
        <QueryErrorResetBoundary>
            {({ reset }) => (
                <ErrorBoundary
                    onReset={reset}
                    fallbackRender={({ error, resetErrorBoundary }) =>
                        showError ? (
                            <ErrorHandler
                                stackTrace={showStackTrace ? error.stack : undefined}
                                onTryAgain={resetErrorBoundary}
                                errorTitle={renderErrorTitle?.(error)}
                                errorMessage={renderErrorMessage?.(error)}
                            />
                        ) : (
                            children
                        )
                    }
                >
                    {children}
                </ErrorBoundary>
            )}
        </QueryErrorResetBoundary>
    );

    if (loader == null) {
        return errorBoundary;
    }

    return <SuspenseBoundary loader={loader}>{errorBoundary}</SuspenseBoundary>;
};
